import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { ApiError } from "@/api/errors";
import { queryKeys } from "@/api/query-keys";
import { openPaywall } from "@/shared/lib/paywall";
import { subscribeNewChat } from "@/shared/lib/newChatSignal";
import { createTransientId } from "@/shared/lib/transientId";
import { type CreateRunRequest, streamChatRun } from "../api/createRun";
import { cancelRun, confirmRun } from "../api/runControls";
import type { AttachmentView } from "../lib/attachments";
import { type ChatStreamEvent, parseChatEvent } from "../lib/sseEvents";
import type { RunState, ToolState } from "../types";
import { useMyConnectors } from "./useMyConnectors";

/** Remember the last model the user picked, so a new thread reopens on it. */
const MODEL_STORAGE_KEY = "anyai-model";
function readStoredModelId(): string | null {
  try {
    return localStorage.getItem(MODEL_STORAGE_KEY);
  } catch {
    return null;
  }
}

interface StartPayload {
  isNew: boolean;
  mode: "append" | "regenerate";
  conversationId: string | null;
  userText: string;
  userAttachments: AttachmentView[];
  parentMessageId: string | null;
}

type Action =
  | { type: "start"; payload: StartPayload }
  | { type: "event"; event: ChatStreamEvent }
  | { type: "fail"; message: string }
  | { type: "reset" };

/**
 * Patch the tool entry with `callId` (the model can chain several calls per
 * turn, so tools are a list). Unknown ids append a defensive placeholder —
 * marked autoApproved so the UI never offers buttons it can't honor.
 */
function upsertTool(
  state: RunState,
  next: Partial<ToolState> & { callId: string },
): RunState {
  const index = state.tools.findIndex((tool) => tool.callId === next.callId);
  if (index === -1) {
    const defaults: Omit<ToolState, "callId"> = {
      proposal: null,
      status: "proposed",
      result: null,
      preview: null,
      resultPreview: null,
      error: null,
      autoApproved: true,
    };
    return { ...state, tools: [...state.tools, { ...defaults, ...next }] };
  }
  const tools = state.tools.slice();
  tools[index] = { ...tools[index], ...next } as ToolState;
  return { ...state, tools };
}

function applyEvent(state: RunState, event: ChatStreamEvent): RunState {
  switch (event.type) {
    case "run":
      return {
        ...state,
        runId: event.runId,
        conversationId: event.conversationId || state.conversationId,
        userMessageId: event.inputMessageId ?? state.userMessageId,
      };
    case "status":
      return { ...state, phase: event.phase };
    case "delta":
      return { ...state, phase: "responding", assistantText: state.assistantText + event.content };
    case "sources":
      return { ...state, sources: event.sources };
    case "error":
      return { ...state, status: "error", error: event.message };
    case "tool_proposal": {
      const autoApproved = Boolean(event.proposal.auto_approved);
      return upsertTool(
        { ...state, status: autoApproved ? "tool_running" : "awaiting_tool" },
        {
          callId: event.proposal.tool_call_id,
          proposal: event.proposal,
          status: "proposed",
          result: null,
          preview: null,
          resultPreview: null,
          error: null,
          autoApproved,
        },
      );
    }
    case "tool_executing":
      return upsertTool({ ...state, status: "tool_running" }, {
        callId: event.toolCallId,
        status: "executing",
      });
    case "tool_succeeded":
      // The model keeps going after a tool resolves (it may chain more calls
      // or stream the answer) — return the run to plain streaming.
      return upsertTool({ ...state, status: "streaming", phase: "responding" }, {
        callId: event.toolCallId,
        status: "succeeded",
        result: event.result,
        preview: event.preview ?? null,
        resultPreview: event.resultPreview ?? null,
      });
    case "tool_failed":
      return upsertTool({ ...state, status: "streaming" }, {
        callId: event.toolCallId,
        status: "failed",
        error: event.error ?? null,
      });
    case "tool_declined":
      return upsertTool({ ...state, status: "streaming" }, {
        callId: event.toolCallId,
        status: "declined",
      });
    case "tool_rejected":
      return upsertTool({ ...state, status: "streaming" }, {
        callId: event.toolCallId,
        status: "rejected",
        error: event.error ?? event.reason ?? null,
      });
    case "tool_expired":
      return upsertTool({ ...state, status: "streaming" }, {
        callId: event.toolCallId,
        status: "expired",
      });
    case "done":
      return {
        ...state,
        status: event.status === "cancelled" ? "cancelled" : "done",
        outputMessageId: event.outputMessageId ?? state.outputMessageId,
      };
  }
}

function reducer(state: RunState | null, action: Action): RunState | null {
  switch (action.type) {
    case "start":
      return {
        isNew: action.payload.isNew,
        mode: action.payload.mode,
        conversationId: action.payload.conversationId,
        runId: null,
        status: "streaming",
        phase: action.payload.mode === "regenerate" ? "thinking" : null,
        userText: action.payload.userText,
        userAttachments: action.payload.userAttachments,
        userMessageId: null,
        outputMessageId: null,
        parentMessageId: action.payload.parentMessageId,
        assistantText: "",
        sources: null,
        tools: [],
        error: null,
      };
    case "event":
      return state ? applyEvent(state, action.event) : state;
    case "fail":
      return state ? { ...state, status: "error", error: action.message } : state;
    case "reset":
      return null;
  }
}

export interface SendOpts {
  conversationId: string | null;
  text: string;
  webSearch?: boolean;
  /** Pre-enable the code-execution tool for this turn (assistant default). */
  codeExecution?: boolean;
  fileIds?: string[];
  attachments?: AttachmentView[];
  /** Scope a brand-new conversation to an assistant (applied only when new). */
  assistantId?: string | null;
  /**
   * Skill ids for this turn, overriding the toggled `activeSkillIds`. Used by
   * the composer's slash flow, where the skill is resolved at send time (a
   * `setActiveSkillIds` call wouldn't reach `skillsRef` before this fires).
   */
  skillIds?: string[];
}

/** A message composed mid-stream, waiting for the current run to finish. */
export interface QueuedSend extends SendOpts {
  queueId: string;
}

export interface ChatRunContextValue {
  run: RunState | null;
  selectedModelId: string | null;
  setSelectedModelId: (id: string) => void;
  send: (opts: SendOpts) => void;
  /** Messages that will auto-send, in order, when the current run ends. */
  queuedSends: QueuedSend[];
  removeQueuedSend: (queueId: string) => void;
  regenerate: (opts: {
    conversationId: string;
    parentMessageId: string;
    webSearch?: boolean;
  }) => void;
  stop: () => void;
  /** Abort and clear the current run (used by "new chat" to recover a stuck thread). */
  reset: () => void;
  confirmTool: (toolCallId: string, action: "approve" | "always" | "cancel") => void;
  /** True while the run is in flight (streaming, awaiting a tool, or executing one). */
  isStreaming: boolean;
  /** Skills explicitly toggled on for upcoming turns (composer picker, max 3). */
  activeSkillIds: string[];
  setActiveSkillIds: Dispatch<SetStateAction<string[]>>;
}

const ChatRunContext = createContext<ChatRunContextValue | null>(null);

/**
 * Owns the single in-flight chat run + the selected model, scoped to the chat
 * route subtree so streaming state survives the welcome → thread navigation.
 * Drives the SSE state machine; on completion it re-fetches the conversation
 * from the server and clears the optimistic run.
 */
export function ChatRunProvider({ children }: { children: ReactNode }) {
  const [run, dispatch] = useReducer(reducer, null);
  const [selectedModelId, setSelectedModelIdState] = useState<string | null>(() =>
    readStoredModelId(),
  );
  const setSelectedModelId = useCallback((id: string) => {
    setSelectedModelIdState(id);
    try {
      localStorage.setItem(MODEL_STORAGE_KEY, id);
    } catch {
      // Storage can throw (private mode, quota) — the in-memory pick still works.
    }
  }, []);
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Connected MCP connectors auto-enable on every persistent run (no composer
  // toggle — they're managed in Settings). Capped server-side at 3 per run.
  const { data: myConnectors } = useMyConnectors();

  // Skills are the opposite: explicitly user-toggled per chat session via the
  // composer picker (and seeded from `?skill=`). Client-state only in V1 —
  // intentionally not persisted server-side.
  const [activeSkillIds, setActiveSkillIds] = useState<string[]>([]);

  // Sends composed mid-stream, drained in order as each run ends.
  const [queuedSends, setQueuedSends] = useState<QueuedSend[]>([]);
  const streamingRef = useRef(false);
  // The conversation a brand-new-thread run created — queued follow-ups
  // composed before navigation must land in it, not spawn another thread.
  const lastConversationIdRef = useRef<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const runRef = useRef<RunState | null>(null);
  const modelRef = useRef<string | null>(null);
  const connectorsRef = useRef<string[]>([]);
  const skillsRef = useRef<string[]>([]);
  runRef.current = run;
  modelRef.current = selectedModelId;
  connectorsRef.current = (myConnectors ?? [])
    .filter((connector) => connector.status === "connected")
    .slice(0, 3)
    .map((connector) => connector.id);
  skillsRef.current = activeSkillIds.slice(0, 3);

  useEffect(() => () => abortRef.current?.abort(), []);

  const finalize = useCallback(
    async (conversationId: string | null) => {
      const tasks = [
        qc.invalidateQueries({ queryKey: queryKeys.conversations.all }),
        qc.invalidateQueries({ queryKey: queryKeys.chat.modelUsage() }),
      ];
      if (conversationId) {
        tasks.push(qc.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) }));
      }
      await Promise.all(tasks);
      dispatch({ type: "reset" });
    },
    [qc],
  );

  const startStream = useCallback(
    (body: CreateRunRequest, payload: StartPayload) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      let navigated = false;
      dispatch({ type: "start", payload });

      streamChatRun(body, {
        signal: controller.signal,
        onEvent: (raw) => {
          const event = parseChatEvent(raw);
          if (!event) return;
          dispatch({ type: "event", event });

          if (event.type === "run" && payload.isNew && event.conversationId && !navigated) {
            navigated = true;
            lastConversationIdRef.current = event.conversationId;
            void navigate({
              to: "/chat/$id",
              params: { id: event.conversationId },
              replace: true,
            });
          }
          if (event.type === "done") {
            const convId = event.conversationId ?? runRef.current?.conversationId ?? payload.conversationId;
            void finalize(convId ?? null);
          }
        },
      }).catch((error: unknown) => {
        if (controller.signal.aborted) {
          dispatch({ type: "reset" });
          return;
        }
        // A gated model reached the backend (the pre-send check was bypassed,
        // e.g. an old premium-model conversation opened on a free plan): open the
        // paywall and drop the failed run instead of leaving an inline error.
        if (
          error instanceof ApiError &&
          error.status === 403 &&
          error.code === "model_not_available"
        ) {
          openPaywall({
            kind: "model",
            modelName: modelRef.current ?? "this model",
            plan:
              error.details?.required_plan === "studio"
                ? "studio"
                : error.details?.required_plan === "power"
                  ? "power"
                  : "creator",
          });
          dispatch({ type: "reset" });
          return;
        }
        // A 402 already opened the upgrade modal inside the SSE client; still
        // surface a readable inline error on the assistant turn.
        const message = error instanceof ApiError ? error.message : "Stream failed";
        dispatch({ type: "fail", message });
      });
    },
    [navigate, finalize],
  );

  const send = useCallback(
    (opts: SendOpts) => {
      const {
        conversationId,
        text,
        webSearch = false,
        codeExecution = false,
        fileIds = [],
        attachments = [],
        assistantId = null,
        skillIds: skillIdsOverride,
      } = opts;
      const model = modelRef.current;
      const content = text.trim();
      // A turn needs either text or at least one uploaded file.
      if (!model || (!content && fileIds.length === 0)) return;
      // Composed while a reply is still streaming → queue; the drain effect
      // fires it (in order) the moment the run ends.
      if (streamingRef.current) {
        setQueuedSends((q) => [...q, { ...opts, queueId: createTransientId("queued-send") }]);
        return;
      }
      const connectorIds = connectorsRef.current;
      // Runs here are always persistent (`temporary: false`) — the backend
      // rejects skill_ids on temporary runs, so this stays safe as-is. The
      // slash flow passes a fresh override (resolved this tick); otherwise we
      // fall back to the toggled selection mirrored in `skillsRef`.
      const skillIds = skillIdsOverride ?? skillsRef.current;
      const body: CreateRunRequest = {
        model,
        mode: "append",
        temporary: false,
        thinking_level: "",
        web_search: webSearch,
        code_execution: codeExecution,
        ...(content ? { content } : {}),
        ...(fileIds.length > 0 ? { file_ids: fileIds } : {}),
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(connectorIds.length > 0 ? { connector_ids: connectorIds.slice(0, 3) } : {}),
        ...(skillIds.length > 0 ? { skill_ids: skillIds.slice(0, 3) } : {}),
        // The assistant only scopes a brand-new conversation; once it exists the
        // conversation carries the assistant server-side.
        ...(!conversationId && assistantId ? { assistant_id: assistantId } : {}),
      };
      startStream(body, {
        isNew: conversationId === null,
        mode: "append",
        conversationId,
        userText: content,
        userAttachments: attachments,
        parentMessageId: null,
      });
    },
    [startStream],
  );

  const regenerate = useCallback(
    ({
      conversationId,
      parentMessageId,
      webSearch = false,
    }: {
      conversationId: string;
      parentMessageId: string;
      webSearch?: boolean;
    }) => {
      const model = modelRef.current;
      if (!model) return;
      const connectorIds = connectorsRef.current;
      const skillIds = skillsRef.current;
      const body: CreateRunRequest = {
        model,
        mode: "regenerate",
        temporary: false,
        thinking_level: "",
        web_search: webSearch,
        code_execution: false,
        conversation_id: conversationId,
        parent_message_id: parentMessageId,
        ...(connectorIds.length > 0 ? { connector_ids: connectorIds.slice(0, 3) } : {}),
        ...(skillIds.length > 0 ? { skill_ids: skillIds.slice(0, 3) } : {}),
      };
      startStream(body, {
        isNew: false,
        mode: "regenerate",
        conversationId,
        userText: "",
        userAttachments: [],
        parentMessageId,
      });
    },
    [startStream],
  );

  const stop = useCallback(() => {
    const current = runRef.current;
    if (current?.runId) void cancelRun(current.runId).catch(() => undefined);
  }, []);

  // Clear the in-flight/errored run and abort its stream. New-chat calls this so
  // a stuck or errored thread doesn't linger over the welcome view (navigation
  // alone doesn't reset an errored `isNew` run).
  const reset = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: "reset" });
  }, []);

  // "New chat" (sidebar button / ⌘⇧O) fires this signal from the app shell,
  // which sits outside this provider — reset the run so a stuck thread doesn't
  // linger over the welcome view.
  useEffect(() => subscribeNewChat(reset), [reset]);

  const confirmTool = useCallback(
    (toolCallId: string, action: "approve" | "always" | "cancel") => {
      const current = runRef.current;
      if (!current?.runId || !toolCallId) return;
      void confirmRun(current.runId, {
        tool_call_id: toolCallId,
        action: action === "cancel" ? "cancel" : "approve",
        always_allow: action === "always",
      }).catch(() => undefined);
      if (action === "always") {
        // Settings' always-allow chips read from this cache.
        void qc.invalidateQueries({ queryKey: queryKeys.connectors.mine() });
      }
    },
    [qc],
  );

  const isStreaming =
    run !== null &&
    (run.status === "streaming" ||
      run.status === "awaiting_tool" ||
      run.status === "tool_running");
  streamingRef.current = isStreaming;

  // Drain: the moment a run ends, fire the next queued message (in order).
  useEffect(() => {
    if (isStreaming || queuedSends.length === 0) return;
    const [next, ...rest] = queuedSends;
    setQueuedSends(rest);
    send({
      ...next,
      conversationId: next.conversationId ?? lastConversationIdRef.current,
    });
  }, [isStreaming, queuedSends, send]);

  const removeQueuedSend = useCallback((queueId: string) => {
    setQueuedSends((q) => q.filter((item) => item.queueId !== queueId));
  }, []);

  const value = useMemo<ChatRunContextValue>(
    () => ({
      run,
      selectedModelId,
      setSelectedModelId,
      send,
      queuedSends,
      removeQueuedSend,
      regenerate,
      stop,
      reset,
      confirmTool,
      isStreaming,
      activeSkillIds,
      setActiveSkillIds,
    }),
    [run, selectedModelId, setSelectedModelId, send, queuedSends, removeQueuedSend, regenerate, stop, reset, confirmTool, isStreaming, activeSkillIds],
  );

  return <ChatRunContext.Provider value={value}>{children}</ChatRunContext.Provider>;
}

export function useChatRun(): ChatRunContextValue {
  const ctx = useContext(ChatRunContext);
  if (!ctx) throw new Error("useChatRun must be used within ChatRunProvider");
  return ctx;
}
